/**
 * Module delivery options — the notification block's declared extension slot.
 *
 * A "delivery option" is a per-user setting that shapes HOW an opted-in
 * notification is delivered, without being a channel toggle: procurement's
 * daily-summary send hour, messaging's email grace period. Channel choices
 * (Notify / Email) belong to the catalog rows and never here.
 *
 * Registered once, keyed by notification-catalog module id, and resolved inside
 * ModuleNotificationBlock — so an option appears in BOTH mounts (the module's
 * own settings page and the central /settings?tab=notifications tab) with no
 * per-mount wiring. That is the whole point of the registry: a module may NOT
 * render such a control beside the block, because a control rendered beside the
 * block exists in only one of the two mounts.
 *
 * Registrations live in `deliveryOptions/index.ts`, which the block imports
 * eagerly. Lazy registration from a module's settings page would leave the
 * option missing in the central tab, which is the bug this slot exists to
 * prevent.
 */
import type { ComponentType } from 'react';

/** Props every delivery-option control receives from the block. */
export interface ModuleDeliveryOptionProps {
  /** Notification-catalog module id the block is rendering. */
  moduleId: string;
}

export interface ModuleDeliveryOption {
  /**
   * Stable id, unique within the module. Used as the React key and as the
   * handle for `unregisterModuleDeliveryOption`.
   */
  id: string;
  Component: ComponentType<ModuleDeliveryOptionProps>;
}

const registry = new Map<string, ModuleDeliveryOption[]>();

/**
 * Register a delivery-option control for a module. Call at import time from
 * `deliveryOptions/index.ts`.
 *
 * Re-registering the same `id` replaces the previous entry rather than
 * duplicating it, so a hot-reloaded module does not stack copies of its own
 * control.
 */
export function registerModuleDeliveryOption(
  moduleId: string,
  option: ModuleDeliveryOption,
): void {
  const existing = registry.get(moduleId) ?? [];
  const next = existing.filter((o) => o.id !== option.id);
  next.push(option);
  registry.set(moduleId, next);
}

/** Remove a registration. Primarily for tests that register a fake control. */
export function unregisterModuleDeliveryOption(moduleId: string, optionId: string): void {
  const existing = registry.get(moduleId);
  if (!existing) return;
  const next = existing.filter((o) => o.id !== optionId);
  if (next.length === 0) registry.delete(moduleId);
  else registry.set(moduleId, next);
}

/** The options registered for a module, in registration order. Never null. */
export function getModuleDeliveryOptions(moduleId: string): ModuleDeliveryOption[] {
  return registry.get(moduleId) ?? [];
}
